import { Button } from "@/components/ui/button";
import type { FooterProps } from "./types";

/**
 * Desktop pagination bar.
 *
 * Hidden below `md:` — the mobile list screens end with an inline
 * `MobileListPagination` row instead, because the bottom edge of the viewport
 * belongs to the tab bar.
 */
export function Footer({
	currentPage = 1,
	totalPages = 1,
	totalEntries = 1,
	entriesPerPage = 10,
	currentPageEntries,
	onPageChange,
}: FooterProps) {
	const startEntry = (currentPage - 1) * entriesPerPage + 1;
	// Use currentPageEntries if provided, otherwise calculate based on entriesPerPage
	const endEntry =
		currentPageEntries !== undefined
			? (currentPage - 1) * entriesPerPage + currentPageEntries
			: Math.min(currentPage * entriesPerPage, totalEntries);

	const handlePrevious = () => {
		if (currentPage > 1 && onPageChange) {
			onPageChange(currentPage - 1);
		}
	};

	const handleNext = () => {
		if (currentPage < totalPages && onPageChange) {
			onPageChange(currentPage + 1);
		}
	};

	return (
		<footer className="hidden h-[76px] w-full border-gray-200 border-t bg-white px-6 md:block">
			<div className="flex h-[76px] w-full flex-row items-center justify-between">
				<div className="flex items-center">
					<span className="text-gray-600 text-sm">
						Showing {startEntry}-{endEntry} of {totalEntries} entries
					</span>
				</div>
				<div className="flex items-center space-x-4">
					<Button
						variant="ghost"
						size="sm"
						onClick={handlePrevious}
						disabled={currentPage <= 1}
						className="min-h-[44px] min-w-[44px] text-gray-600 text-sm hover:bg-gray-100 hover:text-gray-900 disabled:cursor-not-allowed disabled:text-gray-400"
					>
						Previous
					</Button>
					<span className="whitespace-nowrap text-gray-600 text-sm">
						Page {currentPage} of {totalPages}
					</span>
					<Button
						variant="ghost"
						size="sm"
						onClick={handleNext}
						disabled={currentPage >= totalPages}
						className="min-h-[44px] min-w-[44px] text-gray-600 text-sm hover:bg-gray-100 hover:text-gray-900 disabled:cursor-not-allowed disabled:text-gray-400"
					>
						Next
					</Button>
				</div>
			</div>
		</footer>
	);
}
